import { debounce, isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { readSettingsMirror, settingsMirrorKey, writeSettingsMirror } from "./settingsMirror";
import { useGeotabClient } from "@/components/GeotabClientContext";
import AddInIds from "@/constants/AddInIds";
import { useAddinData } from "@/hooks/addins/useAddinData";
import { useUpdateAddIn } from "@/hooks/addins/useUpdateAddIn";
import {
  createUserUiSettingsRecord,
  isUserUiSettingsRecord,
  pickSettings,
  UserUiSettings,
  UserUiSettingsAddInData,
} from "@/models/UserUiSettingsAddInData";

/**
 * How long to wait before storing a change. Dragging a date range or ticking through options
 * produces a burst of updates, and each stored record is a round-trip.
 */
const PERSIST_DELAY_MS = 300;

export interface UserScopedSettings<TSettings extends UserUiSettings> {
  settings: TSettings;
  setSettings: (updater: TSettings | ((current: TSettings) => TSettings)) => void;
  /** Whether the stored settings are known yet. `true` when there is no user to look up. */
  isFetched: boolean;
  isSaving: boolean;
}

/**
 * Remembers one named part of the interface for the signed-in user.
 *
 * The stored record follows the user between browsers and devices; a browser-local copy is kept
 * alongside it purely so a page can render the user's actual settings instead of flashing its
 * defaults for the length of a round-trip.
 *
 * @param scope - Names the part of the interface, e.g. `faultMonitor.dateRange`. Must be stable.
 * @param defaults - The settings' shape and fallback values. Read once, on first render.
 */
export function useUserScopedSettings<TSettings extends UserUiSettings>(
  scope: string,
  defaults: TSettings,
): UserScopedSettings<TSettings> {
  const { me, username, database, isLoading } = useGeotabClient();
  const userId = me?.id;

  // The defaults define the shape, not a value that can change. Holding them in a ref means a
  // caller that builds the object inline does not reset the settings on every render.
  const defaultsRef = useRef(defaults);

  const mirrorKey = useMemo(
    () => settingsMirrorKey(username, database, scope),
    [database, scope, username],
  );

  const { data: records = [], isFetched: recordsFetched } = useAddinData<unknown>(
    AddInIds.userUiSettings,
    !userId,
  );
  const { mutate: persist, isPending: isSaving } = useUpdateAddIn<
    UserUiSettingsAddInData<TSettings>
  >(AddInIds.userUiSettings, "id");

  /**
   * Whether the stored settings are as good as they are going to get.
   *
   * "No user" and "the user is not known yet" look the same from here — `me` is null in both —
   * and treating the second as the first is what let a caller take the defaults for the stored
   * settings, write them back, and lose everything the user had configured. Credentials arrive
   * first, then `me` follows them asynchronously, so both have to have settled.
   */
  const isFetched = isLoading ? false : userId ? recordsFetched : !username;

  const remote = useMemo(() => {
    if (!userId) {
      return null;
    }
    const record = records.find(
      (details): details is UserUiSettingsAddInData<TSettings> =>
        isUserUiSettingsRecord<TSettings>(details, userId, scope),
    );
    return record?.settings ?? null;
  }, [records, scope, userId]);

  const [settings, setSettings] = useState<TSettings>(() =>
    pickSettings({
      defaults: defaultsRef.current,
      mirror: readSettingsMirror<TSettings>(settingsMirrorKey(username, database, scope)),
      remoteLoaded: false,
    }),
  );

  /**
   * Which mirror key the user has edited settings under, or `undefined` for "not yet".
   *
   * Comparing against the current key rather than holding a flag means switching user picks the
   * new user's settings up again without depending on effect ordering. The sentinel must not be
   * `null`, because a key of `null` — no credentials yet — would then read as "already edited"
   * and the stored settings would never be applied.
   */
  const editedForRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (editedForRef.current === mirrorKey) {
      // The user changed something here; the stored copy must not overwrite it.
      return;
    }

    const next = pickSettings({
      defaults: defaultsRef.current,
      mirror: readSettingsMirror<TSettings>(mirrorKey),
      remote,
      remoteLoaded: isFetched,
    });

    setSettings(current => (isSameSettings(current, next) ? current : next));
  }, [isFetched, mirrorKey, remote]);

  /**
   * Dragging a date range produces a burst of changes and each stored record is a round-trip,
   * so only the settled value is written. `flush` writes whatever is still waiting and cancels
   * the pending call in one step — doing that by hand is where a second, duplicate write came
   * from, and lodash gets it right by construction.
   */
  const persistDebounced = useMemo(
    () => debounce(persist, PERSIST_DELAY_MS, { leading: false, trailing: true }),
    [persist],
  );

  useEffect(() => {
    if (editedForRef.current !== mirrorKey) {
      // Only persist what the user changed — never echo back what was just read.
      return;
    }

    // Written straight away, undebounced: this is what makes the choice survive a reload even
    // if the tab goes away before the record is stored.
    writeSettingsMirror(mirrorKey, settings);

    if (!userId) {
      return;
    }

    persistDebounced(createUserUiSettingsRecord<TSettings>(userId, scope, settings));
  }, [mirrorKey, persistDebounced, scope, settings, userId]);

  // A change made in the last few hundred milliseconds before the tab goes away would otherwise
  // survive only in the browser copy, which the stored record overrides on the next visit.
  useEffect(() => {
    const flush = () => persistDebounced.flush();
    const flushWhenHidden = () => {
      if (document.visibilityState === "hidden") {
        flush();
      }
    };

    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", flushWhenHidden);

    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", flushWhenHidden);
      flush();
    };
  }, [persistDebounced]);

  const updateSettings = useCallback<UserScopedSettings<TSettings>["setSettings"]>(
    updater => {
      editedForRef.current = mirrorKey;
      setSettings(current =>
        typeof updater === "function"
          ? (updater as (current: TSettings) => TSettings)(current)
          : updater,
      );
    },
    [mirrorKey],
  );

  return { settings, setSettings: updateSettings, isFetched, isSaving };
}

/**
 * Whether two settings say the same thing.
 *
 * By value, not by identity: a setting can hold an object — the map keeps its viewport in one —
 * and the stored record is rebuilt into fresh objects every time it is re-read, which is every
 * thirty seconds. Compared by identity, that counted as a change and replaced the settings of a
 * page nobody had touched.
 */
function isSameSettings<TSettings extends UserUiSettings>(left: TSettings, right: TSettings) {
  return isEqual(left, right);
}
